import type { BadgeValue } from "@/types/dynamic-detail";
import { ScenarioSummary } from "./workflow";

/**
 * Option lists the document itself provides, keyed by the name a field declares in
 * `document_options`.
 *
 * They travel with the graph because they *are* the graph: a `counter_path` chooses a step
 * of the workflow being edited, so no reference endpoint could answer it, and rebuilding
 * the list client-side would put the server's `attempts.` prefix in a second place. The
 * same reason the declared variables travel with it.
 */
export type DocumentOptions = Record<string, { value: string; label: string }[]>;

/**
 * One variable the workflow declares, as the graph projection serves it.
 *
 * The stored declaration fields (`name`, `type`, `default`, `label`, `description`) fill
 * the drawer form derived from `VariableDeclaration`. The option keys (`path`, `value`,
 * `group`, …) are what the formula autocompletion appends to its process-wide list —
 * same row, two readers, see `graph_view.variable_views` on the server.
 */
export type DeclaredVariable = {
    name: string;
    type: string;
    default?: unknown;
    label?: string | null;
    description?: string | null;
    path: string;
    value: string;
    group?: string;
    is_namespace?: boolean;
    item_type?: string | null;
};

/** A node outlet, already resolved by the server through `effective_ports()`. */
export type GraphPort = {
    name: string;
    label: string;
    description?: string | null;
    /** What sends a card down this outlet, in one line printed under its name (« Qualifications: Won, Lost »). */
    criteria?: string | null;
    is_default: boolean;
    /** `handler` for an outlet the block declares, `behavior` for one a behaviour adds. */
    source: "handler" | "behavior" | string;
    to: string | null;
};

/** Outlets as drawn on the card: declared order, with the default outlet last. */
export function orderedPorts(node: Pick<GraphNode, "ports">): GraphPort[] {
    const ports = node.ports ?? [];
    return [...ports.filter(port => !port.is_default), ...ports.filter(port => port.is_default)];
}

/**
 * One line of a node's configuration, zipped server-side from field spec and value. The
 * handler picks and words the lines that lead (`BaseNodeHandler.summary()`), so the first
 * ones are what the card shows: « Wait 1 day », « AI agent CS Dossier incomplet ».
 */
export type ConfigSummaryEntry = {
    field_name: string;
    label: string;
    field_type: string;
    unit?: string | null;
    value: unknown;
    display_value: string;
};

/** A behaviour flag present on the node, keyed generically so no key is hardcoded. */
export type NodeBehaviorBadge = {
    key: string;
    label: string;
    detail?: string | null;
};

export type GraphNode = {
    id: string;
    type: string;
    type_label: string;
    category: string;
    /** The handler's own description, shown as the node's tooltip. */
    description?: string | null;
    label: string | null;
    suspends: boolean;
    is_entry: boolean;
    is_terminal: boolean;
    config: Record<string, unknown>;
    config_summary: ConfigSummaryEntry[];
    /**
     * The raw behaviour, beside the badges. The badges say a policy is on; this says
     * what it holds, which is what the editor needs to show a policy without inventing
     * its values — and what makes a partial patch expressible.
     */
    behavior: Record<string, unknown>;
    behaviors: NodeBehaviorBadge[];
    ports: GraphPort[];
    /**
     * Absent in phase 1, where placement is computed client-side. Declared now so
     * `layout.ts` reads "stored position, else dagre" and phase 2 can migrate graph
     * by graph without touching the layout contract.
     */
    position?: { x: number; y: number } | null;
};

/**
 * The part of a projection that can be *drawn*: what the layout and the canvas read.
 *
 * A version's view and the editor's draft differ in their header and in nothing the
 * picture depends on, so both are drawn by the same component through this shape. A
 * `GraphView` satisfies it as it stands.
 */
export type GraphDocument = {
    graph: {
        entry_node_id: string | null;
        nodes: GraphNode[];
        /** Declarations this document holds; absent on older fixtures means none. */
        variables?: DeclaredVariable[];
        options?: DocumentOptions;
    };
    /** How many configuration lines the server means a node card to show. */
    card_summary_rows?: number;
    /** What validation found, so a card can show it carries a problem. Absent means none known. */
    issues?: GraphIssue[];
    /** Identity of the drawn document, so reopening the same one hits the layout cache. */
    version?: { id: number } | null;
};

export type GraphIssueSeverity = "error" | "warning" | "info" | string;

export type GraphIssue = {
    code: string;
    severity: GraphIssueSeverity;
    message: string;
    node_id?: string | null;
    port?: string | null;
};

export type GraphPermissions = {
    edit: boolean;
    deploy: boolean;
    simulate: boolean;
};

export type VersionStatus = "draft" | "published" | "deprecated" | string;

export type VersionHeader = {
    id: number;
    workflow_id?: number | null;
    workflow_code: string;
    workflow_name: string;
    version: number;
    status: VersionStatus;
    /** `{label, tone}` of `status`, served by the backend. */
    status_badge?: BadgeValue;
    notes?: string | null;
    /** The version this one was forked from, by number and by id. */
    parent_version?: number | null;
    parent_version_id?: number | null;
    created_by?: { id: number; name: string } | null;
    created_at: string;
    published_at?: string | null;
    scenarios: ScenarioSummary;
    deployed_in: { campaign_code: string; campaign_name: string; campaign_status?: string; slot: string }[];
    cards: { active: number; total: number };
};

/** An edge of the card's actual trace, in the order it was followed. */
export type FollowedEdge = {
    /** 1-based rank of the crossing, which tells a first pass from a third lap. */
    order?: number;
    from: string;
    port: string;
    to: string;
    at?: string | null;
};

export type GraphHighlight = {
    current_node_id?: string | null;
    visited_node_ids?: string[];
    followed_edges?: FollowedEdge[];
};

/** `GET /dialer/workflow-versions/:id/view/` — the presentation projection. */
export type GraphView = {
    version: VersionHeader;
    graph: {
        entry_node_id: string | null;
        nodes: GraphNode[];
        variables?: DeclaredVariable[];
        options?: DocumentOptions;
    };
    issues: GraphIssue[];
    permissions: GraphPermissions;
    /** How many configuration lines the server means a node card to show. */
    card_summary_rows?: number;
    /** Only present on `GET /dialer/cards/:id/graph/`. */
    highlight?: GraphHighlight | null;
};

/** `POST …/nodes/:node_id/validate/` — a dry run that writes nothing. */
export type NodeValidationResult = {
    valid: boolean;
    field_errors: Record<string, string>;
    graph_issues: GraphIssue[];
    ports: GraphPort[];
};

export function findNode(view: GraphView, nodeId: string | null | undefined): GraphNode | undefined {
    if (!nodeId) return undefined;
    return view.graph.nodes.find(node => node.id === nodeId);
}

export function nodeDisplayLabel(node: GraphNode): string {
    return node.label || node.id;
}
